import React, { useState } from 'react';
import { gql } from '@apollo/client';
import apolloClient from '@/components/common/ApolloClient';
import { TCIDropdown } from '@/components/ui/Dropdown';
import cn from '@/utils/cn';
import I18n from 'i18n-js';

interface DrawerUtilityRowProps {
  displayName: string;
  locale: string;
  signOutPath: string;
}

const SAVE_LOCALE = gql`
  mutation SaveLocale($locale: String!) {
    updateLocale(locale: $locale) {
      success
    }
  }
`;

const LANGUAGE_LABELS = { en: 'English', es: 'Español' };
const LANGUAGE_SHORT = { en: 'EN', es: 'ES' };

const DrawerUtilityRow = ({ displayName, locale: initialLocale, signOutPath }: DrawerUtilityRowProps) => {
  const [locale, setLocale] = useState(initialLocale);
  const [loading, setLoading] = useState(false);

  const changeLocale = (newLocale: string) => {
    if (newLocale === locale) return;

    setLocale(newLocale);
    setLoading(true);
    apolloClient.mutate({ mutation: SAVE_LOCALE, variables: { locale: newLocale } })
      .then(() => window.location.reload());
  };

  return (
    <div className={cn(
      'reflow-drawer-utility tw-hidden tw-items-center tw-justify-between',
      'tw-px-3 tw-py-2 tw-bg-[#262340] tw-text-white',
      'tw-border-b tw-border-solid tw-border-white/20',
    )}
    >
      <div className="tw-flex tw-items-center tw-gap-2">
        <TCIDropdown>
          <TCIDropdown.Trigger asChild>
            <button
              className={cn(
                'tw-bg-transparent tw-border-0 tw-text-white tw-cursor-pointer',
                'tw-text-sm tw-p-0 tw-flex tw-items-center tw-gap-1',
                'focus:tw-outline focus:tw-outline-2',
                'focus:tw-outline-white focus:tw-outline-offset-2',
              )}
              type="button"
            >
              {displayName}
              <i aria-hidden className="fa fa-caret-down tw-text-xs" />
            </button>
          </TCIDropdown.Trigger>
          <TCIDropdown.Content align="start" side="bottom" width="unset">
            <TCIDropdown.Item asChild>
              <a
                className="tw-text-sm !tw-text-red !tw-no-underline"
                data-method="delete"
                href={signOutPath}
                rel="nofollow"
              >
                {I18n.t('sign_out')}
              </a>
            </TCIDropdown.Item>
          </TCIDropdown.Content>
        </TCIDropdown>
      </div>
      <TCIDropdown>
        <TCIDropdown.Trigger asChild>
          <button
            aria-label={`${I18n.t('language_selection')} - ${LANGUAGE_LABELS[locale]}`}
            className={cn(
              'tw-bg-transparent tw-text-white tw-cursor-pointer tw-text-sm',
              'tw-px-2 tw-py-1 tw-border tw-border-solid tw-border-white/40',
              'tw-rounded tw-flex tw-items-center tw-gap-1',
              'focus:tw-outline focus:tw-outline-2',
              'focus:tw-outline-white focus:tw-outline-offset-2',
            )}
            disabled={loading}
            type="button"
          >
            {loading && <i aria-hidden className="fa fa-spinner fa-spin tw-mr-1" />}
            {LANGUAGE_SHORT[locale]}
            <i aria-hidden className="fa fa-caret-down tw-text-xs" />
          </button>
        </TCIDropdown.Trigger>
        <TCIDropdown.Content align="end" side="bottom" width="unset">
          {(['en', 'es'] as const).map(lang => (
            <TCIDropdown.Item
              key={lang}
              onSelect={(e) => {
                e.preventDefault();
                changeLocale(lang);
              }}
            >
              <span className="tw-text-sm tw-flex tw-items-center tw-w-[1rem]">
                {locale === lang && <i aria-hidden className="fa fa-check !tw-text-green !tw-text-sm !tw-m-0" />}
              </span>
              <span className="tw-text-sm">{LANGUAGE_LABELS[lang]}</span>
            </TCIDropdown.Item>
          ))}
        </TCIDropdown.Content>
      </TCIDropdown>
    </div>
  );
};

export default DrawerUtilityRow;
